import { useContext, useEffect, useRef, useState } from 'react'
import { Constants } from '@/assets'
import { CartTypes } from '@/modules/Cart/CartReducer'
import { dispatchUpdateCart } from '@/modules/Cart/CartServiceHelper'
import { useEMTState } from '@/store'
import { DispatchContext } from '@/store/context'

export const offerToService = {
  petsitter_home_sitting: 'garde-chez-eux',
  customer_home_sitting: 'garde-chez-vous',
  visit_hour: 'visite',
  visit_half_hour: 'visite',
  walk_hour: 'promenade',
  walk_half_hour: 'promenade',
}

export const serviceToOffer = {
  'garde-chez-eux': 'petsitter_home_sitting',
  'garde-chez-vous': 'customer_home_sitting',
  visite: {
    hour: 'visit_hour',
    half_hour: 'visit_half_hour',
  },
  promenade: {
    hour: 'walk_hour',
    half_hour: 'walk_half_hour',
  },
}

import { DateTime } from 'luxon'

export const transformToCartProducts = (service: string, dateTimeInstances: any[]) => {
  const products: { offer: string; start: string; end: string }[] = []

  dateTimeInstances?.forEach((instance) => {
    if (!instance) return

    if (service === 'promenade' || service === 'visite') {
      instance.timePeriods?.forEach((period: any) => {
        const startDateTime = DateTime.fromISO(instance.date, { zone: 'Europe/Paris' }).set({
          hour: Number(period.timeStart.split(':')[0]),
          minute: Number(period.timeStart.split(':')[1]),
          second: 0,
          millisecond: 0,
        })

        const duration = serviceToOffer[service][period.serviceDuration]

        const minutesToAdd = duration === 'walk_half_hour' || duration === 'visit_half_hour' ? 30 : 60

        const endDateTime = startDateTime.plus({ minutes: minutesToAdd })

        products.push({
          offer: serviceToOffer[service][period.serviceDuration],
          start: startDateTime.toISO() ?? '',
          end: endDateTime.toISO() ?? '',
        })
      })
    } else {
      const start = DateTime.fromISO(instance.dateStart, { zone: 'Europe/Paris' }).set({
        hour: Number(instance.timeStart?.split(':')[0] ?? 0),
        minute: Number(instance.timeStart?.split(':')[1] ?? 0),
        second: 0,
        millisecond: 0,
      })

      const end = DateTime.fromISO(instance.dateEnd, { zone: 'Europe/Paris' }).set({
        hour: Number(instance.timeEnd?.split(':')[0] ?? 0),
        minute: Number(instance.timeEnd?.split(':')[1] ?? 0),
        second: 0,
        millisecond: 0,
      })

      products.push({
        offer: serviceToOffer[service],
        start: start.toISO() ?? '',
        end: end.toISO() ?? '',
      })
    }
  })

  return products
}

interface TimePeriod {
  timeStart: string
  serviceDuration?: 'hour' | 'half_hour'
}

interface ServiceInstance {
  dateStart?: string
  dateEnd?: string
  date?: string
  timeStart?: string
  timeEnd?: string
  timePeriods: TimePeriod[]
  offer?: string
}

type UpdateCartPatch = Partial<{
  latitude?: number
  longitude?: number
  service: string
  dateTimeInstances: any[]
  petsitters: string[]
  animals: string[]
  message: string
  allowAutoRebound: boolean
  allowTenders: boolean
  immediateTenders: boolean
}>

export interface Products {
  offer: string
  start: string
  end: string
}

export const transformFromCartProducts = (products: Products[]) => {
  const serviceValues: Record<string, ServiceInstance[]> = {
    promenade: [],
    'garde-chez-vous': [],
    'garde-chez-eux': [],
    visite: [],
  }

  products.forEach((product) => {
    const serviceKey = offerToService[product.offer]
    if (!serviceKey) return

    if (serviceKey === 'visite') {
      const serviceDuration = product.offer === 'visit_hour' ? 'hour' : 'half_hour'
      const startDate = product.start.split('T')[0]
      const endDate = product.end.split('T')[0]
      const time = product.start.split('T')[1].slice(0, 5)

      const existingInstance = serviceValues.visite.find(
        (inst: any) => inst.dateStart === startDate && inst.dateEnd === endDate,
      )

      if (existingInstance) {
        existingInstance.timePeriods.push({
          timeStart: time,
          serviceDuration: serviceDuration,
        })
      } else {
        serviceValues.visite.push({
          dateStart: startDate,
          dateEnd: endDate,
          offer: product.offer,
          timePeriods: [
            {
              timeStart: time,
              serviceDuration: serviceDuration,
            },
          ],
        })
      }
    } else if (serviceKey === 'promenade') {
      const serviceDuration = product.offer === 'walk_hour' ? 'hour' : 'half_hour'
      const date = product.start.split('T')[0]
      const time = product.start.split('T')[1].slice(0, 5)

      const existingInstance = serviceValues.promenade.find((inst: any) => inst.date === date)

      if (existingInstance) {
        existingInstance.timePeriods.push({
          timeStart: time,
          serviceDuration: serviceDuration,
        })
      } else {
        serviceValues.promenade.push({
          date: date,
          offer: product.offer,
          timePeriods: [
            {
              timeStart: time,
              serviceDuration: serviceDuration,
            },
          ],
        })
      }
    } else {
      serviceValues[serviceKey].push({
        dateStart: product.start.split('T')[0],
        dateEnd: product.end.split('T')[0],
        timeStart: product.start.split('T')[1].slice(0, 5),
        timeEnd: product.end.split('T')[1].slice(0, 5),
        timePeriods: [],
        offer: product.offer,
      })
    }
  })

  return serviceValues
}

const useCart = () => {
  const state = useEMTState()
  const cart = state.cart
  const dispatch = useContext(DispatchContext)
  const operationQueue = useRef<Promise<void>[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const debounceTimeout = useRef<NodeJS.Timeout>()
  const lastUpdateTimestamp = useRef<number>(cart.lastUpdate || Date.now())
  const hasPendingOperations = useRef(false)

  useEffect(() => {
    const processStorageUpdate = (newState: any) => {
      if (newState.cart?.lastUpdate && newState.cart?.lastUpdate > lastUpdateTimestamp.current) {
        lastUpdateTimestamp.current = newState.cart.lastUpdate
        dispatch({ type: CartTypes.Restore, payload: newState.cart })
      }
      hasPendingOperations.current = false
    }

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === `${Constants.storageID}/Store`) {
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current)

        const newState = JSON.parse(e.newValue || '{}')
        hasPendingOperations.current = true

        if (operationQueue.current.length > 0) {
          processStorageUpdate(newState)
          return
        }

        debounceTimeout.current = setTimeout(() => {
          if (hasPendingOperations.current) {
            processStorageUpdate(newState)
          }
        }, 300)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current)
      hasPendingOperations.current = false
    }
  }, [dispatch])

  const clearSelection = () => {
    dispatch({ type: CartTypes.ClearCart, payload: null })
  }

  const updateCart = async (patch: UpdateCartPatch) => {
    try {
      setIsLoading(true)

      const shouldRecomputeProducts =
        typeof patch.service !== 'undefined' && typeof patch.dateTimeInstances !== 'undefined'

      const products = shouldRecomputeProducts
        ? transformToCartProducts(patch.service as string, patch.dateTimeInstances as any[])
        : cart.products || []

      const payload = {
        products,
        petsitters: patch.petsitters ?? cart.petsitters ?? [],
        animals: patch.animals ?? cart.animals ?? [],
        message: patch.message ?? cart.message ?? '',
        allowAutoRebound: patch.allowAutoRebound ?? cart.allowAutoRebound ?? false,
        allowTenders: patch.allowTenders ?? cart.allowTenders ?? true,
        immediateTenders: patch.immediateTenders ?? cart.immediateTenders ?? false,
      }

      const operation = dispatchUpdateCart(dispatch, cart)(payload)
      operationQueue.current.push(operation)

      if (operationQueue.current.length === 1) {
        await Promise.all(operationQueue.current)
        operationQueue.current = []
      }
    } finally {
      setIsLoading(false)
    }
  }

  return {
    clearSelection,
    products: cart.products || [],
    serviceValues: transformFromCartProducts(cart.products || []),
    animals: cart.animals || [],
    updateCart,
    message: cart.message || '',
    rejectedPetsitters: cart.rejectedPetsitters || {},
    isLoading,
    allowAutoRebound: cart.allowAutoRebound ?? false,
    allowTenders: cart.allowTenders ?? true,
  }
}

export default useCart
